import { supabase } from '../lib/supabase';

// Favorites service for user saved businesses
export const favoritesService = {
  // Get user's favorite businesses
  async getUserFavorites(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_favorites')?.select(`id,created_at,business:businesses (id,name,short_description,logo_url,cover_image_url,city,county,average_rating,total_reviews,is_featured,categories (id,name,slug))`)?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) {
        return { data: [], error: error?.message };
      }

      // Transform data to include business details at root level
      const transformedData = data?.map(favorite => ({
        id: favorite?.id,
        created_at: favorite?.created_at,
        ...favorite?.business,
        isFavorited: true
      })) || [];

      return { data: transformedData, error: null };
    } catch (error) {
      const errorMessage = error?.message?.includes('Failed to fetch')
        ? 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' :'Failed to load favorites. Please try again.';
      
      return { data: [], error: errorMessage };
    }
  },

  // Add business to favorites
  async addToFavorites(userId, businessId) {
    try {
      const { data, error } = await supabase
        ?.from('user_favorites')
        ?.insert({
          user_id: userId,
          business_id: businessId,
          created_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      if (error) {
        // Check if it's a duplicate error
        if (error?.code === '23505') {
          return { data: null, error: 'Business is already in your favorites.' };
        }
        return { data: null, error: error?.message };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to add to favorites.' };
    }
  },

  // Remove business from favorites
  async removeFromFavorites(userId, businessId) {
    try {
      const { error } = await supabase
        ?.from('user_favorites')
        ?.delete()
        ?.eq('user_id', userId)
        ?.eq('business_id', businessId);

      if (error) {
        return { error: error?.message };
      }

      return { error: null };
    } catch (error) {
      return { error: 'Failed to remove from favorites.' };
    }
  },

  // Check if business is favorited by user
  async isFavorited(userId, businessId) {
    try {
      const { data, error } = await supabase
        ?.from('user_favorites')
        ?.select('id')
        ?.eq('user_id', userId)
        ?.eq('business_id', businessId)
        ?.single();

      if (error && error?.code !== 'PGRST116') {
        return { isFavorited: false, error: error?.message };
      }

      return { isFavorited: !!data, error: null };
    } catch (error) {
      return { isFavorited: false, error: 'Failed to check favorite status.' };
    }
  },

  // Toggle favorite status
  async toggleFavorite(userId, businessId) {
    try {
      const { isFavorited, error: checkError } = await this.isFavorited(userId, businessId);
      
      if (checkError) {
        return { isFavorited: false, error: checkError };
      }

      if (isFavorited) {
        const { error } = await this.removeFromFavorites(userId, businessId);
        if (error) {
          return { isFavorited: true, error };
        }
        return { isFavorited: false, error: null };
      } else {
        const { error } = await this.addToFavorites(userId, businessId);
        if (error) {
          return { isFavorited: false, error };
        }
        return { isFavorited: true, error: null };
      }
    } catch (error) {
      return { isFavorited: false, error: 'Failed to toggle favorite.' };
    }
  },

  // Get favorites count for user
  async getFavoritesCount(userId) {
    try {
      const { count, error } = await supabase
        ?.from('user_favorites')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('user_id', userId);

      if (error) {
        return { count: 0, error: error?.message };
      }

      return { count: count || 0, error: null };
    } catch (error) {
      return { count: 0, error: 'Failed to get favorites count.' };
    }
  }
};

export default favoritesService;